import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Settings.css";

/* ===== Base API robuste (prod & dev) =====
   - Essaie VITE_API_URL / REACT_APP_API_URL
   - Nettoie les slashs finaux + /api éventuel
   - Sinon, retombe sur le même domaine que le front
*/
const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const API_BASE = (RAW
  ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
  : (typeof window !== "undefined" && window.location.origin) || ""
);

const Settings = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("Email");
  const [password, setPassword] = useState("********");
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");

  // Nouveaux états pour l’abonnement
  const [abonnementRaw, setAbonnementRaw] = useState(""); // "basic" | "standard" | "premium" | ""/null
  const [role, setRole] = useState("");                    // "pro" | "client" | "admin" ...
  const [cancelLoading, setCancelLoading] = useState(false);

  // conservés pour compat mais non utilisés (UI lecture seule)
  const [editEmail, setEditEmail] = useState(false);
  const [editPassword, setEditPassword] = useState(false);

  const userId = typeof window !== "undefined" ? localStorage.getItem("userId") : null;
  const token  = typeof window !== "undefined" ? localStorage.getItem("token")  : null;

  const formatAbonnement = (val) => {
    const s = (val || "").toString().trim().toLowerCase();
    if (!s) return "Aucun";
    if (s === "basic") return "Basic";
    if (s === "standard") return "Standard";
    if (s === "premium") return "Premium";
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/users/${userId}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });
        const data = await res.json();

        if (data?.email) setEmail(data.email);
        if (data?.prenom) setPrenom(data.prenom);
        if (data?.nom) setNom(data.nom);
        if (data?.abonnement !== undefined) setAbonnementRaw(data.abonnement || "");
        if (data?.role) setRole(data.role);
        setPassword("********"); // par sécurité
      } catch (err) {
        console.error("Erreur chargement infos utilisateur :", err);
      }
    };

    if (userId) fetchUserInfo();
  }, [userId, token]);

  // Gardé pour compat, mais plus utilisé (email lecture seule sans bouton)
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = `${API_BASE}/api/users/${userId}/email`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      await res.json().catch(() => null);
      setEditEmail(false);
    } catch (err) {
      console.error("Erreur mise à jour email :", err);
    }
  };

  // Gardé pour compat, mais plus utilisé (on passe par forgot-password)
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = `${API_BASE}/api/users/${userId}/password`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      await res.json().catch(() => null);
      setEditPassword(false);
      setPassword("********");
    } catch (err) {
      console.error("Erreur mise à jour mot de passe :", err);
    }
  };

  // Bouton "Résilier l'abonnement" (stub prêt à être branché)
  const handleCancelSubscription = async () => {
    if (cancelLoading) return;
    if (role !== "pro") {
      alert("Fonction réservée aux comptes PRO.");
      return;
    }
    if (!window.confirm("Confirmer la résiliation de votre abonnement ?")) return;

    setCancelLoading(true);
    try {
      // Essaye un endpoint dédié (à adapter côté backend)
      const endpoints = [
        {
          url: `${API_BASE}/api/abonnement/resilier`,
          opts: { method: "POST" },
        },
        {
          // fallback possible : PATCH sur l'utilisateur
          url: `${API_BASE}/api/users/${userId}/abonnement`,
          opts: {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ abonnement: null }),
          },
        },
      ];

      let success = false;
      for (const { url, opts } of endpoints) {
        try {
          const res = await fetch(url, {
            ...(opts || {}),
            headers: {
              ...(opts?.headers || {}),
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: "include",
          });
          if (res.ok) {
            success = true;
            break;
          }
        } catch (_) {
          // ignore et essaie le prochain endpoint
        }
      }

      if (success) {
        setAbonnementRaw("");
        alert("Votre abonnement a été résilié (mise à jour locale).");
      } else {
        alert(
          "La résiliation n'est pas encore configurée côté API. Le bouton est prêt : il suffira d'activer l'endpoint."
        );
      }
    } finally {
      setCancelLoading(false);
    }
  };

  const isPro = role === "pro";
  const abonnementLabel = formatAbonnement(isPro ? abonnementRaw : "");

  return (
    <div>
      <section className="settings-container">
        <h2 className="settings-title">Paramètres du compte</h2>

        {/* Infos non modifiables */}
        <div className="settings-section">
          <label className="input-label">Prénom</label>
          <div className="input-wrapper">
            <input type="text" value={prenom} readOnly className="input" />
          </div>

          <label className="input-label">Nom</label>
          <div className="input-wrapper">
            <input type="text" value={nom} readOnly className="input" />
          </div>
        </div>

        {/* Email (lecture seule, sans bouton d’édition) */}
        <form className="settings-section" onSubmit={handleEmailSubmit}>
          <label className="input-label">Email</label>
          <div className="input-wrapper">
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly
              className="input"
              placeholder="Entrez votre email"
            />
            <div className="icon-container">
              <iconify-icon icon="ic:outline-mail" width="16" height="16" />
            </div>
          </div>
        </form>

        {/* Mot de passe : bouton redirige vers /forgot_password */}
        <form className="settings-section" onSubmit={handlePasswordSubmit}>
          <label className="input-label">Mot de passe</label>
          <div className="input-wrapper">
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              readOnly
              className="input"
              placeholder="Nouveau mot de passe"
            />
            <div className="icon-container">
              <iconify-icon icon="ic:outline-lock" width="16" height="16" />
            </div>
            <button
              className="btn btn-edit-toggle"
              type="button"
              onClick={() => navigate("/auth/forgot-password", { state: { email } })}
              title="Changer le mot de passe via email"
            >
              ✎
            </button>
          </div>
        </form>

        {/* Abonnement PRO */}
        <div className="settings-section">
          <label className="input-label">Abonnement</label>
          <div className="input-wrapper">
            <input
              type="text"
              value={abonnementLabel}
              readOnly
              className="input"
            />
            <div className="icon-container">
              <iconify-icon icon="mdi:crown-outline" width="16" height="16" />
            </div>

            <button
              className="btn btn-edit-toggle"
              type="button"
              onClick={handleCancelSubscription}
              disabled={!isPro || cancelLoading}
              title={
                isPro
                  ? "Résilier l'abonnement (à brancher à l'API)"
                  : "Disponible uniquement pour les comptes PRO"
              }
              style={{ marginLeft: 8 }}
            >
              {cancelLoading ? "…" : "Résilier"}
            </button>
          </div>
          {!isPro && (
            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
              (Vous n’êtes pas en compte PRO)
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Settings;
